import { Check, CheckCheck, ChevronDown, ChevronRight, MessageSquare, Quote } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import {
  messageTime,
  type ConversationMessage,
  type ConversationThread,
  type MessageActions,
} from '@/lib/conversation';
import { Avatar } from './Avatar';
import { EntityText } from './EntityText';
import { FileChip } from './FileChip';
import { MessageContent } from './MessageContent';
import { Skeleton } from './Skeleton';
import { ResponseTimer } from './ResponseTimer';
import { cn } from './cn';

export function Message({
  message,
  entities,
  people,
  onEntityOpen,
  onAttachmentOpen,
  onReaction,
  responseNow,
  timeZone = 'Europe/Moscow',
}: MessageActions & { message: ConversationMessage }) {
  const titleId = useId();
  return (
    <article
      data-slot="message"
      data-message-id={message.id}
      aria-labelledby={titleId}
      aria-busy={message.pending || undefined}
      className={cn('flex min-w-0 gap-3 p-4', message.pending && 'animate-pop-in')}
    >
      <Avatar name={message.author.name} src={message.author.avatar} size="md" className="shrink-0" />
      <div className="min-w-0 flex-1">
        <header className="flex min-h-6 flex-wrap items-center gap-x-3 gap-y-1">
          <span id={titleId}>
            {onEntityOpen ? (
              <EntityText
                variant="author"
                person={people?.find((person) => person.id === message.author.id)}
                name={message.author.name}
                onClick={(event) => onEntityOpen({ ...message.author, kind: 'user' }, event.currentTarget)}
              />
            ) : (
              <span className="type-subheading">{message.author.name}</span>
            )}
          </span>
          <time
            dateTime={message.sentAt}
            title={messageTime(message.sentAt, timeZone, true)}
            className="text-xs whitespace-nowrap text-fg-muted tabular-nums"
          >
            {messageTime(message.sentAt, timeZone)}
          </time>
          {message.editedAt && (
            <time
              dateTime={message.editedAt}
              title={'Изменено: ' + messageTime(message.editedAt, timeZone, true)}
              className="text-xs text-fg-muted"
            >
              изменено
            </time>
          )}
          {message.pending && <span className="text-xs text-fg-muted">Сохраняем…</span>}
          {message.source && (
            <span
              data-slot="message-source"
              className="ml-auto inline-flex min-w-0 items-center gap-1.5 text-xs text-fg-muted"
            >
              <MessageSquare className="size-3.5 shrink-0" aria-hidden />
              <span className="truncate" title={message.source.label + ' · ' + message.source.channel}>
                {message.source.label} · {message.source.channel}
              </span>
            </span>
          )}
        </header>
        {message.quote && (
          <blockquote data-slot="message-quote" className="mt-3 border-l-2 border-line-strong pl-3">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-fg-muted">
              <Quote className="size-3.5" aria-hidden />
              {message.quote.author}
            </p>
            <MessageContent
              content={message.quote.content}
              entities={entities}
              people={people}
              onEntityOpen={onEntityOpen}
            />
          </blockquote>
        )}
        <div className="mt-2">
          <MessageContent
            content={message.content}
            entities={entities}
            people={people}
            onEntityOpen={onEntityOpen}
          />
        </div>
        {Boolean(message.attachments?.length) && (
          <ul aria-label="Вложения сообщения" className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-2">
            {message.attachments!.map((file) => (
              <li key={file.id}>
                <FileChip
                  file={file}
                  onOpenFrom={onAttachmentOpen ? (trigger) => onAttachmentOpen(file, trigger) : undefined}
                />
              </li>
            ))}
          </ul>
        )}
        {message.response && (
          <div className="mt-3">
            <ResponseTimer wait={message.response} now={responseNow} timeZone={timeZone} />
          </div>
        )}
        {(Boolean(message.reactions?.length) || message.receipt) && (
          <footer className="mt-3 flex flex-wrap items-center gap-2">
            {message.reactions
              ?.filter((reaction) => reaction.count > 0)
              .map((reaction) => (
                <button
                  key={reaction.id}
                  type="button"
                  data-slot="message-reaction"
                  aria-pressed={reaction.selected ?? false}
                  aria-label={
                    reaction.label +
                    ': ' +
                    reaction.count +
                    '. ' +
                    (reaction.selected ? 'Убрать реакцию' : 'Добавить реакцию')
                  }
                  disabled={!onReaction}
                  onClick={() => onReaction?.(message.id, reaction.id)}
                  title={reaction.people?.join(', ')}
                  className={cn(
                    'inline-flex h-6 items-center gap-1.5 rounded-full border px-2 text-xs tabular-nums transition-colors duration-fast focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-default',
                    reaction.selected
                      ? 'border-brand-fg/40 bg-brand-soft text-brand-fg'
                      : 'border-line bg-card text-fg-muted hover:bg-hover',
                  )}
                >
                  <span aria-hidden>{reaction.emoji}</span>
                  <span>{reaction.count}</span>
                </button>
              ))}
            {message.receipt && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-fg-muted">
                {message.receipt === 'sent' ? (
                  <Check className="size-3.5" aria-hidden />
                ) : (
                  <CheckCheck className="size-3.5" aria-hidden />
                )}
                {message.receipt === 'read'
                  ? 'Прочитано'
                  : message.receipt === 'delivered'
                    ? 'Доставлено'
                    : 'Отправлено'}
              </span>
            )}
          </footer>
        )}
      </div>
    </article>
  );
}

export function MessageThread({
  thread,
  expanded,
  onExpandedChange,
  ...actions
}: MessageActions & {
  thread: ConversationThread;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const regionId = useId();
  const replies = thread.replies ?? [];
  const last = replies.at(-1);
  const people = [...new Map(replies.map((reply) => [reply.author.id, reply.author])).values()];
  return (
    <div data-slot="message-thread">
      <Message message={thread.message} {...actions} />
      {replies.length > 0 && (
        <div className="ml-11 pb-4 pl-4">
          <button
            type="button"
            data-slot="thread-toggle"
            aria-expanded={expanded}
            aria-controls={regionId}
            onClick={() => onExpandedChange(!expanded)}
            className="inline-flex min-h-8 max-w-full flex-wrap items-center gap-2 rounded-sm px-2 text-xs font-medium text-brand-fg hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            {expanded ? (
              <ChevronDown className="size-3.5" aria-hidden />
            ) : (
              <ChevronRight className="size-3.5" aria-hidden />
            )}
            <span>
              {expanded ? 'Свернуть ветку' : 'Ответы в ветке'} · {replies.length}
            </span>
            {!expanded && (
              <span
                data-slot="thread-participants"
                role="img"
                aria-label={'Участники ветки: ' + people.map((person) => person.name).join(', ')}
                title={people.map((person) => person.name).join(', ')}
                className="inline-flex items-center gap-1.5"
              >
                <span aria-hidden className="inline-flex items-center gap-1.5">
                  {people.slice(0, 1).map((person) => (
                    <Avatar
                      key={person.id}
                      name={person.name}
                      src={person.avatar}
                      size="sm"
                      className="ring-2 ring-card"
                    />
                  ))}
                  {people.length > 1 && (
                    <span className="relative inline-grid size-6 place-items-center rounded-full border border-line bg-muted text-2xs text-fg-muted ring-2 ring-card">
                      +{people.length - 1}
                    </span>
                  )}
                </span>
              </span>
            )}
            {last && (
              <span className="font-normal text-fg-muted">
                последний в {messageTime(last.sentAt, actions.timeZone)}
              </span>
            )}
          </button>
          <div
            id={regionId}
            role="region"
            aria-label={'Ответы на сообщение ' + thread.message.author.name}
            hidden={!expanded}
            className="mt-3 border-l border-line"
          >
            <ol>
              {replies.map((reply) => (
                <li key={reply.id}>
                  <Message message={reply} {...actions} />
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

/** A known two-line message reserves exactly the same header/body geometry (R31). */
export function MessageSkeleton({ still = false }: { still?: boolean }) {
  return (
    <div data-slot="message-skeleton" aria-hidden className="flex gap-3 p-4">
      <Skeleton still={still} className="size-8 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1">
        <div className="flex h-6 items-center gap-3">
          <Skeleton still={still} className="w-32" />
          <Skeleton still={still} className="w-10" />
        </div>
        <div className="mt-2 flex h-10 flex-col justify-around">
          <Skeleton still={still} className="w-full" />
          <Skeleton still={still} className="w-3/4" />
        </div>
      </div>
    </div>
  );
}

export function ConversationFeed({
  children,
  label = 'Лента переписки',
}: {
  children: ReactNode;
  label?: string;
}) {
  return (
    <ol data-slot="conversation-feed" aria-label={label} className="min-w-0 divide-y divide-line">
      {children}
    </ol>
  );
}
